import { Checkbox, Dialog, FormControlLabel, ListItem, Paper, useMediaQuery } from '@material-ui/core';
import Authorize from 'components/Authorize';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import { FlagEntityTypes } from 'features/flag/flagsConsts';
import { FlagAsset } from 'features/vehicles/Flags/FlagAsset';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AutoSizer } from 'react-virtualized';
import { VariableSizeList } from 'react-window';
import mobileBreakpoint from 'utils/mobileBreakpoint';
import EmptyPlaceholder from '../../../../components/EmptyPlaceholder/EmptyPlaceholder';
import LoadCard from '../LoadCard';
import LoadGroupDetails from '../LoadGroupDetails';
import { loadsListStyle } from './LoadsListStyle';

function LoadsList({
  loads,
  readinessForLoads,
  contactsForLoad,
  reload,
  selectedRows,
  toggleRowSelected,
  toggleSelectAllFiltered,
  canSeeFlags,
}) {
  const classes = loadsListStyle();
  const [selectedLoad, setSelectedLoad] = useState();
  const history = useHistory();
  const isMobile = useMediaQuery(mobileBreakpoint);

  const outerRef = useRef(null);
  const listRef = useRef(null);

  // cache heights: { [itemId]: number }
  const sizeMapRef = useRef(new Map());

  // Keep selectedLoad fresh if loads refresh
  useEffect(() => {
    if (selectedLoad) {
      setSelectedLoad(loads.find((l) => l.id == selectedLoad.id));
    }
  }, [loads]);

  const openLoad = useCallback(
    (load) => {
      history.push(`/load/group/${load.groupId}`);
    },
    [history]
  );

  // Reset cache when layout/data changes
  useEffect(() => {
    sizeMapRef.current = new Map();
    listRef.current?.resetAfterIndex(0, true);
  }, [isMobile, loads.length]);

  // Observe container resizing (sidebar/layout changes)
  useEffect(() => {
    if (!outerRef.current) return;

    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // don’t wipe cache on every resize; just recompute layout
        listRef.current?.resetAfterIndex(0, true);
      });
    });

    ro.observe(outerRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  const getItemSize = useCallback(
    (index) => {
      const id = loads[index]?.id;
      return (id && sizeMapRef.current.get(id)) ?? (isMobile ? 330 : 310);
    },
    [loads, isMobile]
  );

  const filteredIds = loads.map((l) => l.id);
  const selectedFilteredCount = filteredIds.filter((id) => selectedRows.includes(id)).length;
  const allFilteredSelected =
    filteredIds.length > 0 && selectedFilteredCount === filteredIds.length;
  const someFilteredSelected =
    selectedFilteredCount > 0 && selectedFilteredCount < filteredIds.length;

  const Row = ({ index, style }) => {
    const rowInnerRef = useRef(null);
    const load = loads[index];
    const itemId = load?.id;

    useLayoutEffect(() => {
      const el = rowInnerRef.current;
      if (!el || !itemId) return;

      let rafId = 0;
      const THRESHOLD_PX = 2; // ignore tiny +/- 1px changes that cause loops

      const measureAndStore = () => {
        const next = Math.ceil(el.getBoundingClientRect().height);
        const prev = sizeMapRef.current.get(itemId);

        if (!next) return;

        // Only update if the change is meaningful
        if (prev == null || Math.abs(prev - next) >= THRESHOLD_PX) {
          sizeMapRef.current.set(itemId, next);
          listRef.current?.resetAfterIndex(index);
        }
      };

      // Measure after paint to avoid “early 250px” cases
      rafId = requestAnimationFrame(measureAndStore);

      // Observe changes in the row size (async content, fonts, etc.)
      const ro = new ResizeObserver(() => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(measureAndStore);
      });

      ro.observe(el);

      return () => {
        cancelAnimationFrame(rafId);
        ro.disconnect();
      };
      // IMPORTANT: don’t depend on `load` object, only stable keys
    }, [index, itemId, isMobile, load?.latestFlag]);

    return (
      <div style={{ ...style, width: '100%', minWidth: 0 }}>
        <div ref={rowInnerRef}>
          <ListItem
            className={`${classes.listItem} ${
              canSeeFlags && load?.latestFlag ? classes.flaggedLoad : ''
            }`}
            selected={selectedLoad?.id === load.id}
          >
            {toggleRowSelected && <Authorize profile={permissionProfiles.LOAD.LOAD_BULK_ACTIONS}>
              <Checkbox
                checked={selectedRows.includes(load?.id)}
                onChange={(e, checked) => toggleRowSelected(checked, load.id)}
              />
            </Authorize>}

            <FlagAsset
              iconOnly
              selectedRows={load?.loads?.map((l) => l.id)}
              flagOn={load?.latestFlag}
              flagObj={load?.latestFlag}
              getFlagFromApi={false}
              allData={load?.loads?.map((l) => ({
                ...l,
                vehicleId: l?.assets?.[0]?.id,
              }))}
              entityType={FlagEntityTypes.TRANSPORT}
              readonly
            />

            <LoadCard
              isGroup
              showJourney={!isMobile}
              load={load} readiness={readinessForLoads?.[load?.id]}
              contacts={contactsForLoad?.[load?.id]}
              onViewClick={() => openLoad(load)}
            />
          </ListItem>
        </div>
      </div>
    );
  };

  return (
    <>
      {!loads?.length && <EmptyPlaceholder className={classes.listWrapper} />}

      {!!loads?.length && (
        <Paper className={classes.listWrapper} ref={outerRef}>
          {toggleRowSelected && toggleSelectAllFiltered && (
            <Authorize profile={permissionProfiles.LOAD.LOAD_BULK_ACTIONS}>
              <div className={classes.selectAllBar}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected}
                      onChange={() => toggleSelectAllFiltered()}
                      color="primary"
                    />
                  }
                  label="Select all"
                />
              </div>
            </Authorize>
          )}
          <div className={classes.listScrollArea}>
            <AutoSizer>
              {({ height, width }) => (
                <VariableSizeList
                  ref={listRef}
                  className="List"
                  height={height}
                  width={width}
                  itemCount={loads.length}
                  itemSize={getItemSize}
                  estimatedItemSize={isMobile ? 330 : 310}
                  itemKey={(index) => loads[index]?.id ?? index}
                >
                  {Row}
                </VariableSizeList>
              )}
            </AutoSizer>
          </div>
        </Paper>
      )}

      <Dialog onClose={() => setSelectedLoad(undefined)} open={!!selectedLoad} maxWidth="lg" fullWidth>
        {!!selectedLoad ? (
          <LoadGroupDetails
            loadGroup={selectedLoad}
            readinessForLoads={readinessForLoads}
            reload={reload}
          />
        ) : null}
      </Dialog>
    </>
  );
}

export default LoadsList;
